import {
  Form,
  Input,
  Button,
  Switch,
  Select,
  Spin,
  DatePicker,
  Space,
  Typography,
  InputNumber,
  List,
  Tag,
  Table,
  Card,
  message,
  Modal,
} from "antd";
import { useEffect, useState } from "react";
import { useNavigate, useLocation, useParams } from "react-router-dom";
import {
  PlusOutlined,
  LoadingOutlined,
  EyeOutlined,
  EyeInvisibleOutlined,
} from "@ant-design/icons";
import { useDispatch, useSelector } from "react-redux";
import moment from "moment";
import StandardFormLayout from "../../layouts/StandardFormLayout/StandardFormLayout";
import "../../styles/teamMember.scss";
import {
  CREATE_VEHICLE,
  UPDATE_VEHICLE,
  FETCH_ALL_FLEET_CATEGORIES,
  FETCH_ALL_VEHICLE_MAKE,
  FETCH_DETAIL_VEHICLE,
  NEW_VEHICLE,
  FETCH_VEHICLE_HISTORY,
  setCategories,
  setDetailVehicle,
  setVehicleMakes,
} from "../../store/fleet-store/reducer";
import {
  FETCH_ALL_FLEET_PRESTART,
  setFleetPrestarts,
} from "../../store/fleet-prestart-store/reducer";
import ModalFleetVehicles from "./components/ModalFleetVehicles";

const { Text: AntText } = Typography;

const antIcon = <LoadingOutlined style={{ fontSize: 50 }} spin />;
const EditTeamMember = () => {
  let navigate = useNavigate();
  const [form] = Form.useForm();
  const { vehicleId } = useParams();
  const { pathname } = useLocation();
  const {
    organisationId,
    _id: userId,
    email: userEmail,
  } = useSelector(({ auth }) => auth.user);
  const { detailVehicle, categories, makes } = useSelector(
    ({ fleets }) => fleets.fleetReducer
  );
  const { fleetPrestarts } = useSelector(
    ({ fleetPreStart }) => fleetPreStart.fleetPreStartReducer
  );
  const [loading, setLoading] = useState(false);
  const dispatch = useDispatch();
  const [initialLoading, setInitialLoading] = useState(true);
  const [isModalVisible, setIsModalVisible] = useState({
    visible: false,
    type: "",
  });
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [history, setHistory] = useState([]);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [showHistory, setShowHistory] = useState(false);
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);

  // Check if the selected category uses hours tracking
  const isHoursCategory = () => {
    if (!selectedCategory || !categories) return false;
    const category = categories.find((cat) => cat._id === selectedCategory);
    return category && category.type === "hours";
  };

  // History table columns configuration
  const historyColumns = [
    {
      title: "Action",
      dataIndex: "action",
      key: "action",
      width: isMobile ? 60 : 100,
      render: (action) => (
        <Tag
          color={
            action === "create" ? "green" : action === "update" ? "blue" : "red"
          }
          size="small"
        >
          {action === "create"
            ? "Created"
            : action === "update"
            ? "Updated"
            : "Deleted"}
        </Tag>
      ),
    },
    {
      title: "Changed By",
      dataIndex: "changedByEmail",
      key: "changedByEmail",
      width: isMobile ? 80 : 200,
      render: (email) => (
        <AntText
          strong
          style={{
            fontSize: isMobile ? "12px" : "14px",
            display: "block",
            overflow: "hidden",
            textOverflow: "ellipsis",
            whiteSpace: "nowrap",
            maxWidth: isMobile ? "50px" : "180px",
          }}
          title={email || "System"}
        >
          {email || "System"}
        </AntText>
      ),
    },
    {
      title: "Date & Time",
      dataIndex: "timestamp",
      key: "timestamp",
      width: isMobile ? 100 : 150,
      render: (timestamp) => (
        <AntText
          type="secondary"
          style={{
            fontSize: isMobile ? "12px" : "14px",
            display: "block",
            overflow: "hidden",
            textOverflow: "ellipsis",
            whiteSpace: "nowrap",
            maxWidth: isMobile ? "90px" : "140px",
          }}
          title={
            isMobile
              ? moment(timestamp).format("DD/MM/YY HH:mm")
              : moment(timestamp).format("DD MMM YYYY HH:mm")
          }
        >
          {isMobile
            ? moment(timestamp).format("DD/MM/YY HH:mm")
            : moment(timestamp).format("DD MMM YYYY HH:mm")}
        </AntText>
      ),
    },
    // Only show these columns on desktop
    ...(isMobile
      ? []
      : [
          {
            title: "Reason",
            dataIndex: "changeReason",
            key: "changeReason",
            width: 200,
            render: (reason) => (
              <AntText
                style={{
                  fontSize: "14px",
                  display: "block",
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  whiteSpace: "nowrap",
                  maxWidth: "180px",
                }}
                title={reason || "No reason provided"}
              >
                {reason || "No reason provided"}
              </AntText>
            ),
          },
          {
            title: "Changes",
            dataIndex: "changes",
            key: "changes",
            width: 600,
            render: (changes, record) => {
              if (record.action === "update" && changes) {
                // Field name mapping to display proper names
                const fieldNameMap = {
                  autoNumber: "Auto Number",
                  nickName: "Nick Name",
                  registration: "Registration",
                  make: "Make",
                  category: "Category",
                  fuelType: "Fuel Type",
                  vinNumber: "VIN Number",
                  enginNumber: "Engine Number",
                  year: "Year",
                  seats: "Seats",
                  TARE: "TARE",
                  GVM: "GVM",
                  color: "Color",
                  tollId: "Toll ID",
                  lastOdometer: "Last Odometer",
                  lastOdometerDate: "Last Odometer Date",
                  nextServiceOdometer: "Next Service Odometer",
                  lastHourMeter: "Last Hour Meter",
                  lastHourMeterDate: "Last Hour Meter Date",
                  nextServiceHourMeter: "Next Service Hour Meter",
                  notes: "Notes",
                  driveUrl: "Drive URL",
                  hourlyCost: "Hourly Cost",
                  status: "Status",
                  fleetPreStart: "Fleet Prestart",
                };

                return (
                  <div>
                    {Object.entries(changes).map(([field, change]) => {
                      // Skip action and fields entries
                      if (field === "action" || field === "fields") {
                        return null;
                      }

                      // Format date values for display
                      const formatValue = (value) => {
                        if (!value) return "(empty)";

                        // Check if it's a date field
                        if (
                          field === "lastOdometerDate" ||
                          field === "lastHourMeterDate"
                        ) {
                          return moment(value).format("DD MMM YYYY");
                        }

                        return value;
                      };

                      return (
                        <div
                          key={field}
                          style={{
                            marginBottom: isMobile ? 2 : 4,
                            fontSize: isMobile ? "12px" : "14px",
                          }}
                        >
                          <AntText
                            strong
                            style={{
                              fontWeight: 600,
                              fontSize: isMobile ? "12px" : "14px",
                            }}
                          >
                            {fieldNameMap[field] || field}:
                          </AntText>
                          <AntText
                            type="secondary"
                            style={{
                              marginLeft: isMobile ? 4 : 8,
                              fontSize: isMobile ? "12px" : "14px",
                              display: isMobile ? "block" : "inline",
                            }}
                          >
                            {isMobile ? (
                              <>
                                <div>from: {formatValue(change.from)}</div>
                                <div>to: {formatValue(change.to)}</div>
                              </>
                            ) : (
                              `from ${formatValue(
                                change.from
                              )} → to ${formatValue(change.to)}`
                            )}
                          </AntText>
                        </div>
                      );
                    })}
                  </div>
                );
              }
              return <AntText type="secondary">-</AntText>;
            },
          },
        ]),
  ];

  // Helper function to compare values (similar to backend logic)
  const compareValues = (oldValue, newValue) => {
    // Handle null/undefined cases
    if (oldValue === null && newValue === null) return true;
    if (oldValue === undefined && newValue === undefined) return true;
    if (oldValue === null && newValue === undefined) return true;
    if (oldValue === undefined && newValue === null) return true;

    // Handle empty string cases
    if (oldValue === "" && newValue === null) return true;
    if (oldValue === null && newValue === "") return true;
    if (oldValue === "" && newValue === undefined) return true;
    if (oldValue === undefined && newValue === "") return true;

    // Handle number/string conversion for numeric fields
    if (typeof oldValue === "number" && typeof newValue === "string") {
      return oldValue === parseFloat(newValue);
    }
    if (typeof oldValue === "string" && typeof newValue === "number") {
      return parseFloat(oldValue) === newValue;
    }

    // Handle boolean/string conversion
    if (typeof oldValue === "boolean" && typeof newValue === "string") {
      return oldValue === (newValue === "true" || newValue === "1");
    }
    if (typeof oldValue === "string" && typeof newValue === "boolean") {
      return (oldValue === "true" || oldValue === "1") === newValue;
    }

    // Handle date comparison (normalize to date only, ignore time)
    if (oldValue instanceof Date && newValue instanceof Date) {
      const oldDate = new Date(oldValue);
      const newDate = new Date(newValue);
      oldDate.setHours(0, 0, 0, 0);
      newDate.setHours(0, 0, 0, 0);
      return oldDate.getTime() === newDate.getTime();
    }

    // Handle string date comparison (for moment objects or date strings)
    if (typeof oldValue === "string" && typeof newValue === "string") {
      // Check if both values look like dates
      const isDateString = (str) => {
        return (
          str && (str.includes("-") || str.includes("/") || str.includes("T"))
        );
      };

      if (isDateString(oldValue) && isDateString(newValue)) {
        const oldDate = new Date(oldValue);
        const newDate = new Date(newValue);

        // Check if dates are valid
        if (!isNaN(oldDate.getTime()) && !isNaN(newDate.getTime())) {
          oldDate.setHours(0, 0, 0, 0);
          newDate.setHours(0, 0, 0, 0);
          return oldDate.getTime() === newDate.getTime();
        }
      }
    }

    // Handle moment object comparison
    if (
      oldValue &&
      typeof oldValue === "object" &&
      oldValue._isAMomentObject &&
      newValue &&
      typeof newValue === "object" &&
      newValue._isAMomentObject
    ) {
      // Compare only the date part (YYYY-MM-DD)
      const oldDateStr = oldValue.format("YYYY-MM-DD");
      const newDateStr = newValue.format("YYYY-MM-DD");
      return oldDateStr === newDateStr;
    }

    // Handle moment object vs string comparison
    if (
      oldValue &&
      typeof oldValue === "object" &&
      oldValue._isAMomentObject &&
      typeof newValue === "string"
    ) {
      const oldDateStr = oldValue.format("YYYY-MM-DD");
      const newDate = new Date(newValue);
      if (!isNaN(newDate.getTime())) {
        const newDateStr = newDate.toISOString().split("T")[0]; // Get YYYY-MM-DD
        return oldDateStr === newDateStr;
      }
    }

    // Handle string vs moment object comparison
    if (
      typeof oldValue === "string" &&
      newValue &&
      typeof newValue === "object" &&
      newValue._isAMomentObject
    ) {
      const oldDate = new Date(oldValue);
      if (!isNaN(oldDate.getTime())) {
        const oldDateStr = oldDate.toISOString().split("T")[0]; // Get YYYY-MM-DD
        const newDateStr = newValue.format("YYYY-MM-DD");
        return oldDateStr === newDateStr;
      }
    }

    // Default strict equality
    return oldValue === newValue;
  };

  // Helper function to check if form data has changed
  const hasFormDataChanged = (formValues, originalData) => {
    if (!originalData) return true; // New vehicle, always submit

    const fieldsToCheck = [
      "autoNumber",
      "nickName",
      "registration",
      "make",
      "category",
      "fleetPreStart",
      "fuelType",
      "vinNumber",
      "enginNumber",
      "year",
      "seats",
      "TARE",
      "GVM",
      "color",
      "tollId",
      "lastOdometer",
      "lastOdometerDate",
      "nextServiceOdometer",
      "lastHourMeter",
      "lastHourMeterDate",
      "nextServiceHourMeter",
      "notes",
      "driveUrl",
      "hourlyCost",
      "status",
    ];

    for (const field of fieldsToCheck) {
      const originalValue = originalData[field];
      const formValue = formValues[field];

      // Special handling for ObjectId fields (make, category, fleetPreStart)
      let originalFieldValue = originalValue;
      if (field === "make" && originalData.make?._id) {
        originalFieldValue = originalData.make._id;
      } else if (field === "category" && originalData.category?._id) {
        originalFieldValue = originalData.category._id;
      } else if (field === "fleetPreStart" && originalData.fleetPreStart?._id) {
        originalFieldValue = originalData.fleetPreStart._id;
      }

      if (!compareValues(originalFieldValue, formValue)) {
        return true; // Found a change
      }
    }
    return false; // No changes found
  };

  const onFinish = (values) => {
    // Check if this is a new vehicle
    if (vehicleId === "new") {
      setLoading(true);
      values.organisationId = organisationId;
      values._id = detailVehicle._id;

      // Add user information for history tracking
      values.userId = userId;
      values.userEmail = userEmail;
      values.changedBy = userId;
      values.changedByEmail = userEmail;
      values.changeReason = "Vehicle created";

      CREATE_VEHICLE(values).then((data) => {
        setLoading(false);
        navigate("/fleet/vehicle");
      });
      return;
    }

    // For existing vehicles, check if data has changed
    if (!hasFormDataChanged(values, detailVehicle)) {
      // Show a message to user that no changes were made and navigate back to list
      message.info("No changes detected.");
      navigate("/fleet/vehicle");
      return;
    }

    setLoading(true);
    values.organisationId = organisationId;
    values._id = detailVehicle._id;

    // Add user information for history tracking
    values.userId = userId;
    values.userEmail = userEmail;
    values.changedBy = userId;
    values.changedByEmail = userEmail;
    values.changeReason = "Vehicle updated";

    UPDATE_VEHICLE(values).then((data) => {
      setLoading(false);
      navigate("/fleet/vehicle");
    });
  };

  const fetchVehicleHistory = async () => {
    if (vehicleId !== "new" && vehicleId) {
      setHistoryLoading(true);
      try {
        const historyData = await FETCH_VEHICLE_HISTORY(
          vehicleId,
          organisationId
        );
        setHistory(historyData);
      } catch (error) {
        console.error("Error fetching vehicle history:", error);
      } finally {
        setHistoryLoading(false);
      }
    }
  };

  const fetchSubData = async () => {
    const fleetCategoryData = await FETCH_ALL_FLEET_CATEGORIES(organisationId);
    if (fleetCategoryData) {
      dispatch(setCategories(fleetCategoryData));
    }
    const vehicleMakeData = await FETCH_ALL_VEHICLE_MAKE(organisationId);
    if (vehicleMakeData) {
      dispatch(setVehicleMakes(vehicleMakeData));
    }
    const fleetPreStartData = await FETCH_ALL_FLEET_PRESTART(organisationId);
    if (fleetPreStartData) {
      dispatch(setFleetPrestarts(fleetPreStartData));
    }
  };

  useEffect(() => {
    const updateState = async () => {
      await fetchSubData();
      if (vehicleId === "new") {
        dispatch(NEW_VEHICLE());
        setInitialLoading(false);
      } else {
        FETCH_DETAIL_VEHICLE(vehicleId).then((data) => {
          dispatch(setDetailVehicle(data));
          setInitialLoading(false);
        });
        // Fetch vehicle history for existing vehicles
        fetchVehicleHistory();
      }
    };
    updateState();
  }, [dispatch, vehicleId]);

  // Handle window resize for responsive design
  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth < 768);
    };

    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  // Track category selection changes
  useEffect(() => {
    if (detailVehicle && detailVehicle.category) {
      setSelectedCategory(detailVehicle.category._id);
    }
  }, [detailVehicle]);

  useEffect(() => {
    if (detailVehicle) {
      form.setFields([
        {
          name: "autoNumber",
          value: detailVehicle.autoNumber,
        },
        {
          name: "nickName",
          value: detailVehicle.nickName,
        },
        {
          name: "registration",
          value: detailVehicle.registration,
        },
        {
          name: "fleetPreStart",
          value: detailVehicle.fleetPreStart?._id,
        },
        {
          name: "make",
          value: detailVehicle.make?._id,
        },
        {
          name: "category",
          value: detailVehicle.category?._id,
        },
        {
          name: "year",
          value: detailVehicle.year,
        },
        {
          name: "seats",
          value: detailVehicle.seats,
        },
        {
          name: "TARE",
          value: detailVehicle.TARE,
        },
        {
          name: "GVM",
          value: detailVehicle.GVM,
        },
        {
          name: "color",
          value: detailVehicle.color,
        },
        {
          name: "tollId",
          value: detailVehicle.tollId,
        },
        {
          name: "lastOdometer",
          value: detailVehicle.lastOdometer,
        },
        {
          name: "lastOdometerDate",
          value: moment(detailVehicle.lastOdometerDate),
        },
        {
          name: "nextServiceOdometer",
          value: detailVehicle.nextServiceOdometer,
        },
        {
          name: "lastHourMeter",
          value: detailVehicle.lastHourMeter,
        },
        {
          name: "lastHourMeterDate",
          value: moment(detailVehicle.lastHourMeterDate),
        },
        {
          name: "nextServiceHourMeter",
          value: detailVehicle.nextServiceHourMeter,
        },
        {
          name: "notes",
          value: detailVehicle.notes,
        },
        {
          name: "driveUrl",
          value: detailVehicle.driveUrl,
        },
        {
          name: "hourlyCost",
          value: detailVehicle.hourlyCost,
        },
        {
          name: "vinNumber",
          value: detailVehicle.vinNumber,
        },
        {
          name: "enginNumber",
          value: detailVehicle.enginNumber,
        },
        {
          name: "fuelType",
          value: detailVehicle.fuelType,
        },
        {
          name: "status",
          value: detailVehicle.status,
        },
      ]);
    }
  }, [detailVehicle]);

  return (
    <>
      <StandardFormLayout
        className={`${showHistory ? "standard-history-layout" : ""}`}
        hierachy={[
          { key: 1, label: "Fleet Vehicle", path: "/fleet/vehicle" },
          { key: 2, label: pathname.indexOf("new") === -1 ? "Edit" : "New" },
        ]}
        editPath="/fleet/vehicle/new"
        form={
          initialLoading ? (
            <Spin spinning={initialLoading} indicator={antIcon}></Spin>
          ) : (
            <Form
              labelAlign="left"
              requiredMark={false}
              labelCol={{ span: 5 }}
              wrapperCol={{ span: 12 }}
              form={form}
              onFinish={onFinish}
              className="member-form modal-sub"
            >
              <div className="personal">
                {/* firstName */}
                <Form.Item
                  label="Auto Number"
                  name="autoNumber"
                  rules={[{ required: true }]}
                >
                  <Input type="number" />
                </Form.Item>

                <Form.Item
                  label="Nick Name"
                  name="nickName"
                  rules={[{ required: true }]}
                >
                  <Input />
                </Form.Item>
                {/* lastName */}
                <Form.Item
                  label="Registration"
                  name="registration"
                  rules={[{ required: true }]}
                >
                  <Input />
                </Form.Item>

                <Form.Item label="Fleet Prestart" name="fleetPreStart">
                  <Select
                    showSearch
                    optionFilterProp="children"
                    filterOption={(input, option) =>
                      option?.children
                        ?.toLowerCase()
                        .includes(input?.toLowerCase())
                    }
                    filterSort={(optionA, optionB) =>
                      optionA?.children
                        ?.toLowerCase()
                        .localeCompare(optionB?.children?.toLowerCase())
                    }
                  >
                    {fleetPrestarts &&
                      fleetPrestarts.map((fleetPreStart) => (
                        <Select.Option
                          key={fleetPreStart?._id}
                          value={fleetPreStart?._id}
                        >
                          {fleetPreStart.title}
                        </Select.Option>
                      ))}
                  </Select>
                </Form.Item>

                <Form.Item label="Vehicle Makes" name="make">
                  <Select
                    showSearch
                    optionFilterProp="children"
                    filterOption={(input, option) =>
                      option?.children
                        ?.toLowerCase()
                        ?.includes(input?.toLowerCase())
                    }
                    filterSort={(optionA, optionB) =>
                      optionA?.children
                        ?.toLowerCase()
                        ?.localeCompare(optionB?.children?.toLowerCase())
                    }
                    dropdownRender={(menu) => (
                      <>
                        {menu}
                        <Space align="right" style={{ padding: "0 8px 4px" }}>
                          <Typography.Link
                            onClick={() =>
                              setIsModalVisible({
                                visible: true,
                                type: "Vehicle Makes",
                              })
                            }
                            style={{
                              whiteSpace: "nowrap",
                            }}
                          >
                            <PlusOutlined /> Add item
                          </Typography.Link>
                        </Space>
                      </>
                    )}
                  >
                    {makes &&
                      makes.map((make) => (
                        <Select.Option key={make?._id} value={make?._id}>
                          {make.description}
                        </Select.Option>
                      ))}
                  </Select>
                </Form.Item>
                <div>
                  {isModalVisible.visible && (
                    <ModalFleetVehicles
                      form={form}
                      fetchSubData={fetchSubData}
                      isModalVisible={isModalVisible}
                      setIsModalVisible={setIsModalVisible}
                    />
                  )}
                </div>

                <Form.Item label="Category" name="category">
                  <Select
                    showSearch
                    optionFilterProp="children"
                    filterOption={(input, option) =>
                      option?.children
                        ?.toLowerCase()
                        ?.includes(input?.toLowerCase())
                    }
                    filterSort={(optionA, optionB) =>
                      optionA?.children
                        ?.toLowerCase()
                        ?.localeCompare(optionB?.children?.toLowerCase())
                    }
                    onChange={(value) => setSelectedCategory(value)}
                    dropdownRender={(menu) => (
                      <>
                        {menu}
                        <Space align="center" style={{ padding: "0 8px 4px" }}>
                          <Typography.Link
                            onClick={() =>
                              setIsModalVisible({
                                visible: true,
                                type: "Category",
                              })
                            }
                            style={{ whiteSpace: "nowrap" }}
                          >
                            <PlusOutlined /> Add item
                          </Typography.Link>
                        </Space>
                      </>
                    )}
                  >
                    {categories &&
                      categories.map((categorie) => (
                        <Select.Option
                          key={categorie?._id}
                          value={categorie?._id}
                        >
                          {categorie.description}
                        </Select.Option>
                      ))}
                  </Select>
                </Form.Item>

                <Form.Item label="Fuel Type" name="fuelType">
                  <Select
                    showSearch
                    optionFilterProp="children"
                    filterOption={(input, option) =>
                      option?.children
                        ?.toLowerCase()
                        ?.includes(input?.toLowerCase())
                    }
                    filterSort={(optionA, optionB) =>
                      optionA?.children
                        ?.toLowerCase()
                        ?.localeCompare(optionB?.children?.toLowerCase())
                    }
                  >
                    <Select.Option value="petrol">Petrol</Select.Option>
                    <Select.Option value="gas">Gas</Select.Option>
                    <Select.Option value="diesel">Diesel</Select.Option>
                  </Select>
                </Form.Item>

                {/* Status  */}
                <Form.Item label="Status" name="status" valuePropName="checked">
                  <Switch
                    checkedChildren="Active"
                    unCheckedChildren="Inactive"
                  />
                </Form.Item>
              </div>
              <div className="personal">
                <Form.Item label="Year" name="year">
                  <Input />
                </Form.Item>

                <Form.Item label="Seats" name="seats">
                  <Input />
                </Form.Item>

                <Form.Item label="Vin Number" name="vinNumber">
                  <Input />
                </Form.Item>

                <Form.Item label="Engin Number" name="enginNumber">
                  <Input />
                </Form.Item>

                <Form.Item label="TARE" name="TARE">
                  <Input />
                </Form.Item>

                <Form.Item label="GVM" name="GVM">
                  <Input />
                </Form.Item>

                <Form.Item label="Color" name="color">
                  <Input />
                </Form.Item>

                <Form.Item label="TollId" name="tollId">
                  <Input />
                </Form.Item>

                {!isHoursCategory() ? (
                  <>
                    <Form.Item label="Last Odometer" name="lastOdometer">
                      <InputNumber min={0} style={{ width: "100%" }} />
                    </Form.Item>

                    <Form.Item
                      label="Last Odometer Date"
                      name="lastOdometerDate"
                    >
                      <DatePicker format="DD MMM YYYY" />
                    </Form.Item>

                    <Form.Item
                      label="Next Service Odometer"
                      name="nextServiceOdometer"
                    >
                      <InputNumber min={0} style={{ width: "100%" }} />
                    </Form.Item>
                  </>
                ) : (
                  <>
                    <Form.Item label="Last Hour Meter" name="lastHourMeter">
                      <InputNumber min={0} style={{ width: "100%" }} />
                    </Form.Item>

                    <Form.Item
                      label="Last Hour Meter Date"
                      name="lastHourMeterDate"
                    >
                      <DatePicker format="DD MMM YYYY" />
                    </Form.Item>

                    <Form.Item
                      label="Next Service Hour Meter"
                      name="nextServiceHourMeter"
                    >
                      <InputNumber min={0} style={{ width: "100%" }} />
                    </Form.Item>
                  </>
                )}

                <Form.Item label="Notes" name="notes">
                  <Input />
                </Form.Item>

                <Form.Item label="Drive Url" name="driveUrl">
                  <Input />
                </Form.Item>

                <Form.Item label="Hourly Cost" name="hourlyCost">
                  <Input />
                </Form.Item>
              </div>
              {/* checkbox-person */}

              <div className="personal" style={{ textAlign: "center" }}>
                <Form.Item>
                  <Space>
                    <Button type="primary" htmlType="submit" loading={loading}>
                      Submit
                    </Button>
                    {vehicleId !== "new" && (
                      <Button
                        type="default"
                        icon={
                          showHistory ? (
                            <EyeInvisibleOutlined />
                          ) : (
                            <EyeOutlined />
                          )
                        }
                        onClick={() => setShowHistory(!showHistory)}
                      >
                        {showHistory ? "Hide History" : "Show History"}
                      </Button>
                    )}
                  </Space>
                </Form.Item>
              </div>
            </Form>
          )
        }
      />
      {vehicleId !== "new" && showHistory && (
        <div
          style={{
            backgroundColor: "#f5f5f5",
            padding: isMobile ? 16 : 40,
            borderRadius: 8,
            margin: "16px 0",
          }}
        >
          <Card>
            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                marginBottom: 16,
                flexWrap: "wrap",
                gap: 8,
              }}
            >
              <Typography.Title level={isMobile ? 5 : 4} style={{ margin: 0 }}>
                Vehicle History
              </Typography.Title>
              <AntText
                type="secondary"
                style={{ fontSize: isMobile ? "12px" : "14px" }}
              >
                {history.length} record(s)
              </AntText>
            </div>
            <Spin spinning={historyLoading}>
              {isMobile ? (
                // Mobile: Display as cards
                <div
                  style={{ display: "flex", flexDirection: "column", gap: 12 }}
                >
                  {history.map((record) => (
                    <Card
                      key={record._id}
                      size="small"
                      style={{
                        marginBottom: 8,
                        minHeight: "100px",
                        padding: "12px",
                      }}
                      bodyStyle={{ padding: "12px" }}
                    >
                      <div style={{ marginBottom: 0 }}>
                        <div
                          style={{
                            display: "flex",
                            justifyContent: "space-between",
                            alignItems: "center",
                            marginBottom: 8,
                          }}
                        >
                          <Tag
                            color={
                              record.action === "create"
                                ? "green"
                                : record.action === "update"
                                ? "blue"
                                : "red"
                            }
                            size="small"
                          >
                            {record.action === "create"
                              ? "Created"
                              : record.action === "update"
                              ? "Updated"
                              : "Deleted"}
                          </Tag>
                          <AntText
                            type="secondary"
                            style={{ fontSize: "12px" }}
                          >
                            {moment(record.timestamp).format("DD/MM/YY HH:mm")}
                          </AntText>
                        </div>

                        <div style={{ marginBottom: 8 }}>
                          <AntText strong style={{ fontSize: "12px" }}>
                            Changed By:{" "}
                          </AntText>
                          <AntText style={{ fontSize: "12px" }}>
                            {record.changedByEmail || "System"}
                          </AntText>
                        </div>

                        <div style={{ marginBottom: 8 }}>
                          <AntText strong style={{ fontSize: "12px" }}>
                            Reason:{" "}
                          </AntText>
                          <AntText style={{ fontSize: "12px" }}>
                            {record.changeReason || "No reason provided"}
                          </AntText>
                        </div>

                        {record.action === "update" && record.changes && (
                          <div>
                            <AntText
                              strong
                              style={{
                                fontSize: "12px",
                                display: "block",
                                marginBottom: 8,
                              }}
                            >
                              Changes:
                            </AntText>
                            {Object.entries(record.changes).map(
                              ([field, change]) => {
                                // Skip action and fields entries
                                if (field === "action" || field === "fields") {
                                  return null;
                                }

                                // Field name mapping to display proper names
                                const fieldNameMap = {
                                  autoNumber: "Auto Number",
                                  nickName: "Nick Name",
                                  registration: "Registration",
                                  make: "Make",
                                  category: "Category",
                                  fuelType: "Fuel Type",
                                  vinNumber: "VIN Number",
                                  enginNumber: "Engine Number",
                                  year: "Year",
                                  seats: "Seats",
                                  TARE: "TARE",
                                  GVM: "GVM",
                                  color: "Color",
                                  tollId: "Toll ID",
                                  lastOdometer: "Last Odometer",
                                  lastOdometerDate: "Last Odometer Date",
                                  nextServiceOdometer: "Next Service Odometer",
                                  lastHourMeter: "Last Hour Meter",
                                  lastHourMeterDate: "Last Hour Meter Date",
                                  nextServiceHourMeter:
                                    "Next Service Hour Meter",
                                  notes: "Notes",
                                  driveUrl: "Drive URL",
                                  hourlyCost: "Hourly Cost",
                                  status: "Status",
                                  fleetPreStart: "Fleet Prestart",
                                };

                                // Format date values for display
                                const formatValue = (value) => {
                                  if (!value) return "(empty)";

                                  // Check if it's a date field
                                  if (
                                    field === "lastOdometerDate" ||
                                    field === "lastHourMeterDate"
                                  ) {
                                    return moment(value).format("DD MMM YYYY");
                                  }

                                  return value;
                                };

                                return (
                                  <div
                                    key={field}
                                    style={{
                                      marginBottom: 6,
                                      padding: 6,
                                      backgroundColor: "#f8f9fa",
                                      borderRadius: 4,
                                      fontSize: "12px",
                                    }}
                                  >
                                    <AntText
                                      strong
                                      style={{
                                        fontWeight: 600,
                                        fontSize: "12px",
                                      }}
                                    >
                                      {fieldNameMap[field] || field}:
                                    </AntText>
                                    <div style={{ marginTop: 4 }}>
                                      <AntText
                                        type="secondary"
                                        style={{ fontSize: "12px" }}
                                      >
                                        from: {formatValue(change.from)}
                                      </AntText>
                                      <br />
                                      <AntText
                                        type="secondary"
                                        style={{ fontSize: "12px" }}
                                      >
                                        to: {formatValue(change.to)}
                                      </AntText>
                                    </div>
                                  </div>
                                );
                              }
                            )}
                          </div>
                        )}
                      </div>
                    </Card>
                  ))}
                </div>
              ) : (
                // Desktop: Display as table
                <Table
                  columns={historyColumns}
                  dataSource={history}
                  rowKey="_id"
                  scroll={{ x: 800 }}
                  size="small"
                  pagination={false}
                  style={{
                    fontSize: "14px",
                    width: "auto",
                  }}
                />
              )}
            </Spin>
          </Card>
        </div>
      )}
    </>
  );
};

export default EditTeamMember;
